import { useEffect, useState } from 'react';
import type { ReferralResponse } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/lib/stores/authStore';
import { ForwardSheet } from '@/components/ForwardSheet/ForwardSheet';
import { ReferralScreenView, type AsyncState } from './ReferralScreenView';

/**
 * Container Экрана 2.11.4 «Реферальная программа» — Block 11 Phase 11.D
 * (ADR-0094, ADR-0005). **Последний экран Блока 11.**
 *
 * Держит state + side effects; UI — чистый presenter `ReferralScreenView`.
 *
 * **Источник данных (мок):** `GET /api/referral` (`apiClient.referral.get()`) —
 * персональная ссылка + счётчики. Реальный эндпоинт (генерация кода, учёт
 * рефералов, правило «1/мес» бонуса) — Region API / билинг-бэк (Фаза 1).
 *
 * **Приватность (P-009):** контейнер хранит и отдаёт ТОЛЬКО счётчики
 * (`referralsCount`/`paidReferralsCount`) — никаких ПДн друзей.
 *
 * **Шаринг:** кнопка «Поделиться» открывает shared `ForwardSheet` («Пригласить
 * друга»: WhatsApp/Telegram/MAX/«Скопировать сообщение»). Системный
 * `navigator.share` не используем — показывал чужие контакты и не имел MAX (тот
 * же мотив, что в карточках декора/фурнитуры). Текст пересылки = приглашение +
 * персональная ссылка; «Скопировать сообщение» кладёт весь этот текст в буфер.
 *
 * **5 состояний (P-021):** idle → loading → success | empty | error.
 * `empty` = успешный ответ с `referralsCount===0` (ссылка показывается, статус —
 * «Пока никто не присоединился»).
 */

const REFERRAL_SHARE_MESSAGE =
  'Добрый день, пользуйтесь Бесплатным сервисом по поиску декоров, фурнитуры, магазинов, калькулятор мебели, раскрой';

export function ReferralScreen() {
  const jwt = useAuthStore((s) => s.jwt);

  const [state, setState] = useState<AsyncState>('idle');
  const [data, setData] = useState<ReferralResponse | null>(null);
  const [open, setOpen] = useState(false);

  // Mount-fetch реферального статуса.
  useEffect(() => {
    if (jwt === null) {
      setState('error');
      return;
    }
    let cancelled = false;
    setState('loading');
    apiClient.referral
      .get()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setState(res.referralsCount === 0 ? 'empty' : 'success');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [jwt]);

  // Текст пересылки = приглашение (message) + персональная ссылка (url) РАЗДЕЛЬНО;
  // ForwardSheet склеит их через пробел для WhatsApp/MAX/копирования и положит
  // чистую ссылку в Telegram `url=`. До data ссылки нет → пустая строка.
  const inviteUrl = data?.inviteUrl ?? '';

  // «Скопировать сообщение» — пишем в буфер ПЕРЕДАННЫЙ текст (сообщение + ссылка),
  // не только URL. clipboard может быть недоступен (insecure context) — молча игнор.
  const onCopyMessage = (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).catch(() => {
        // clipboard недоступен/запрещён — без подтверждения (не критично).
      });
    }
  };

  return (
    <>
      <ReferralScreenView
        state={state}
        inviteUrl={data?.inviteUrl ?? null}
        referralsCount={data?.referralsCount ?? 0}
        paidReferralsCount={data?.paidReferralsCount ?? 0}
        bonusAvailable={data?.bonusAvailable ?? false}
        onShare={() => setOpen(true)}
      />
      <ForwardSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Пригласить друга"
        message={REFERRAL_SHARE_MESSAGE}
        url={inviteUrl}
        onCopyMessage={onCopyMessage}
      />
    </>
  );
}
